import { useState, useRef } from 'react'
import { ActionCardModel, QuestionData, QuestionTypes } from '@playbooks/action-cards-utils/src'

import { EMAIL_REG_EXP, ON_ACTION_CARD_SAVE_EVENT } from '../constants/index'
import { saveLocalData } from '../services/storage'
import { ActionCardsElement } from '../../../action-cards/src/hooks/useActionCardsApiRegister'
import useMountEffect from './useMountEffect'

function useEmailControl({
  loggedInUserEmail,
  actionCardsElementRef,
  playbookId,
}: {
  loggedInUserEmail: string | null
  actionCardsElementRef: React.RefObject<ActionCardsElement | null>
  playbookId: string
}) {
  const [isPolicyAccepted, setIsPolicyAccepted] = useState(!!loggedInUserEmail)
  const [webPlayEmail, setWebPlayEmail] = useState<string | null>(null)
  const [isInvalid, setIsInvalid] = useState<boolean>(false)
  const [isWebPlayEmailInputVisible, setIsWebPlayEmailInputVisible] = useState(true)
  const forceWebPlayEmailVisibility = useRef(false)

  function syncWebPlayEmail() {
    if (loggedInUserEmail && EMAIL_REG_EXP.test(loggedInUserEmail)) {
      setIsWebPlayEmailInputVisible(false)
      setWebPlayEmail(loggedInUserEmail)
      return
    }

    const actionCardData = actionCardsElementRef.current?.getValue()

    if (!actionCardData) return

    const searchService = new ActionCardModel(actionCardData).getSearchService()

    const firstVisibleEmailNode = searchService
      .filterVisible()
      .find(node => (node.data as QuestionData).type === 'Email')
    const firstAnyEmailNode = searchService.find(node => (node.data as QuestionData).type === 'Email')

    const firstVisibleEmailNodeWithValue = searchService
      .filterVisible()
      .find(node => (node.data as QuestionData).type === 'Email' && !!(node.data as QuestionData).value)

    const firstAnyEmailNodeWithValue = searchService.find(
      node => (node.data as QuestionData).type === 'Email' && !!(node.data as QuestionData).value,
    )

    const emailNode =
      firstVisibleEmailNodeWithValue || firstVisibleEmailNode || firstAnyEmailNodeWithValue || firstAnyEmailNode

    if (!emailNode) {
      // if email node isn't found (more likely isn't added)
      // we shouldn't set webPlay email to undefined
      // in other cases - value of the email in action card has to be synchronized with the email-input in the popup
      setWebPlayEmail(null)
      setIsWebPlayEmailInputVisible(!EMAIL_REG_EXP.test('') || forceWebPlayEmailVisibility.current)
      return
    }

    const email = emailNode.getValue()
    setWebPlayEmail(email)
    setIsWebPlayEmailInputVisible(!EMAIL_REG_EXP.test(email) || forceWebPlayEmailVisibility.current)
  }

  useMountEffect(() => {
    Promise.resolve().then(syncWebPlayEmail)

    window.addEventListener(ON_ACTION_CARD_SAVE_EVENT, syncWebPlayEmail)
    return () => {
      window.removeEventListener(ON_ACTION_CARD_SAVE_EVENT, syncWebPlayEmail)
    }
  })

  return {
    isInvalid,
    webPlayEmail,
    setWebPlayEmail: (email: string) => {
      forceWebPlayEmailVisibility.current = true
      setWebPlayEmail(email)
      if (!email || EMAIL_REG_EXP.test(email)) {
        setIsInvalid(false)
        const ac = actionCardsElementRef.current!.getValue()!
        const searchService = new ActionCardModel(ac).getSearchService()

        const firstVisibleEmailNode = searchService
          .filterVisible()
          .find(node => (node.data as QuestionData<string>).type === QuestionTypes.EMAIL)

        const firstAnyEmailNode = searchService.find(
          node => (node.data as QuestionData<string>).type === QuestionTypes.EMAIL,
        )

        const emailNode = firstVisibleEmailNode || firstAnyEmailNode

        if (!emailNode) return

        const updatedData = actionCardsElementRef.current!.partialUpdate({
          isLocked: !!ac?.isLocked,
          room: '',
          data: {
            [emailNode.data.id]: {
              value: email,
            },
          },
        })

        saveLocalData(updatedData, playbookId)
      } else {
        setIsInvalid(true)
      }
    },
    isPolicyAccepted,
    setIsPolicyAccepted,
    isWebPlayEmailInputVisible,
    isWebPlayEmailValid: webPlayEmail && !!EMAIL_REG_EXP.test(webPlayEmail),
  }
}

export default useEmailControl
